<!--
  SupplierIntegrationCard -- one supplier row on
  Distributor > Account > Suppliers (was Integrations).

  WHAT A SUPPLIER CONNECTION IS FOR. The credential prices the catalog at the
  distributor's negotiated rates and, where the back office supports it,
  places blank-goods orders on the distributor's OWN supplier account. The
  card says whether that account is linked and, when it is, which one -- by
  account number. It never shows the login or the secret.

  ORDERS ARE SANMAR-ONLY TODAY. tf-deconetwork fetches a distributor's
  credentials for its POs for SanMar only (ResolveSupplierCredentialsForOrder-
  Action); our S&S callback exists but is not called yet. So the copy for any
  other supplier promises PRICING, never orders -- see `placesOrders`.

  ONE VISUAL LANGUAGE (Jake, 2026-10-07: "I'm not convinced we should have
  those big logo cards for suppliers at all. We don't have one for
  Stripe"). The structure, classes and row layout are the Stripe Connect
  card's (StripeConnectCard.vue), shared through ./integration-card.css
  rather than copied. The OrderWrite chrome is gone with the logo: no bolt
  icon and no "Smart Supplier" label. Dropping the logo also retires the
  #4089 no-logo fallback -- there is no bundled image left to be missing.

  Test ids are keyed on `supplierKey` (vendors.supplier_key), not the
  display name, so a seed that prefixes names ("[TEST] SanMar") still
  resolves them.
-->
<template>
  <v-card class="tf-connect-card" :data-testid="`supplier-integration-card-${testKey}`">
    <v-card-title>{{ name }}</v-card-title>
    <v-card-subtitle>
      {{
        placesOrders
          ? `Lets ThreadFlow price and order ${name} blanks on your own account.`
          : `Lets ThreadFlow price ${name} blanks at your own account's rates.`
      }}
    </v-card-subtitle>

    <v-card-text>
      <div class="tf-connect-row">
        <div class="tf-connect-logo tf-connect-logo--supplier" aria-hidden="true">
          {{ monogram }}
        </div>

        <div class="tf-connect-body">
          <div class="tf-connect-identity">
            <span
              v-if="isConnected && accountNumber"
              class="tf-connect-account"
              :data-testid="`supplier-integration-account-${testKey}`"
            >
              Account {{ accountNumber }}
            </span>
            <span v-else-if="isConnected" class="tf-connect-account">
              {{ name }} account connected
            </span>
            <span v-else class="tf-connect-account tf-connect-account--muted">
              No {{ name }} account connected
            </span>

            <v-chip
              :color="isConnected ? 'success' : 'grey'"
              size="small"
              label
              :data-testid="`supplier-integration-status-${testKey}`"
              class="tf-connect-badge"
            >
              {{ isConnected ? "Connected" : "Not connected" }}
            </v-chip>
          </div>

          <div class="tf-connect-meta">
            {{ metaLine }}
          </div>
        </div>

        <div v-if="isConnected" class="tf-connect-ctas">
          <v-btn
            variant="outlined"
            size="small"
            :data-testid="`supplier-integration-edit-${testKey}`"
            class="tf-connect-cta"
            @click="edit"
          >
            <v-icon start>edit</v-icon>
            Edit
          </v-btn>

          <v-btn
            variant="text"
            color="error"
            size="small"
            :data-testid="`supplier-integration-disconnect-${testKey}`"
            class="tf-connect-cta"
            @click="disconnect"
          >
            <v-icon start>link_off</v-icon>
            Disconnect
          </v-btn>
        </div>

        <div v-else class="tf-connect-ctas">
          <v-btn
            variant="flat"
            color="primary"
            size="small"
            :data-testid="`supplier-integration-connect-${testKey}`"
            class="tf-connect-cta"
            @click="connect"
          >
            <v-icon start>arrow_forward</v-icon>
            Connect {{ name }}
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { SUPPLIERS_ORDERED_ON_DISTRIBUTOR_ACCOUNT } from "./supplierAccounts";

export default {
  name: "SupplierIntegrationCard",

  props: {
    supplierID: {
      type: Number,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    // vendors.supplier_key ("sanmar", "ss_activewear"). Null for a vendor
    // that predates supplier keys; the card then keys its test ids on the id.
    supplierKey: {
      type: String,
      default: null,
    },
    isConnected: {
      type: Boolean,
      required: true,
    },
    // The only part of the stored credential the browser receives.
    accountNumber: {
      type: String,
      default: null,
    },
  },

  emits: ["connect", "edit", "disconnect"],

  computed: {
    placesOrders() {
      return SUPPLIERS_ORDERED_ON_DISTRIBUTOR_ACCOUNT.includes(this.supplierKey);
    },

    metaLine() {
      if (this.isConnected) {
        return this.placesOrders
          ? "Orders and pricing use your account."
          : "Pricing uses your account.";
      }
      return this.placesOrders
        ? "Connect to use your own account and negotiated pricing."
        : "Connect to use your negotiated pricing.";
    },

    testKey() {
      return this.supplierKey || String(this.supplierID);
    },

    /**
     * The small tile in the Stripe card's logo slot. A short leading token
     * that is not a plain word is kept whole ("S&S"); otherwise the
     * capitals of the first word ("SanMar" -> "SM"), else its first letter.
     */
    monogram() {
      const first = (this.name || "").replace(/^\[[^\]]*\]\s*/, "").trim().split(/\s+/)[0] || "";
      if (first.length <= 3 && /[^A-Za-z]/.test(first)) return first;
      const capitals = first.replace(/[^A-Z]/g, "");
      if (capitals.length >= 2) return capitals.slice(0, 2);
      return first.charAt(0).toUpperCase();
    },
  },

  methods: {
    edit() {
      this.$emit("edit", this.supplierID);
    },
    disconnect() {
      this.$emit("disconnect", this.supplierID);
    },
    connect() {
      this.$emit("connect", this.supplierID);
    },
  },
};
</script>

<style scoped src="./integration-card.css"></style>

<style scoped>
/* Supplier tiles use the brand navy; the purple belongs to Stripe. */
.tf-connect-logo--supplier {
  background: var(--tf-secondary, #133752);
}
</style>
